import { useEffect, useState } from "react";
import { Mail, MessageCircle, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Panel } from "@/components/shared";
import { api, errMsg, useApi } from "@/lib/api";

const blank = { id: null, name: "", channel: "WHATSAPP", subject: "", body: "" };

export function TemplatesTab() {
  const { data, reload } = useApi("/settings/templates");
  const [f, setF] = useState(null);
  useEffect(() => { if (data?.templates?.length && !f) setF(data.templates[0]); }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    try {
      const r = f.id ? await api.put(`/settings/templates/${f.id}`, f) : await api.post("/settings/templates", f);
      toast.success("Template saved");
      setF(r.data);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    try { await api.delete(`/settings/templates/${f.id}`); toast.success("Template deleted"); setF(null); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const insertVar = (v) => setF({ ...f, body: `${f.body}{{${v}}}` });
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="space-y-1.5" data-testid="template-list">
        <Button variant="outline" className="w-full mb-2" onClick={() => setF(blank)} data-testid="new-template-button"><Plus className="h-4 w-4 mr-2" />New template</Button>
        {data?.templates.map((t) => (
          <button key={t.id} onClick={() => setF(t)} data-testid={`template-item-${t.id}`}
            className={`w-full text-left rounded-md border px-3 py-2.5 text-sm flex items-center gap-2 ${f?.id === t.id ? "bg-secondary/60 border-foreground/25" : "hover:bg-secondary/30"}`}>
            {t.channel === "EMAIL" ? <Mail className="h-4 w-4 text-sky-500" /> : <MessageCircle className="h-4 w-4 text-emerald-500" />}
            <span className="flex-1 truncate">{t.name}</span><span className="text-[10px] text-muted-foreground">{t.channel}</span>
          </button>
        ))}
      </div>
      {f && (
        <Panel className="lg:col-span-2 min-w-0" title={f.id ? "Edit template" : "New template"}
          action={f.id && <Button size="sm" variant="ghost" onClick={del} data-testid="delete-template-button"><Trash2 className="h-4 w-4" /></Button>}>
          <div className="p-5 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Assembly Ready" data-testid="template-name-input" /></div>
              <div className="space-y-1.5"><Label>Channel</Label>
                <Select value={f.channel} onValueChange={(v) => setF({ ...f, channel: v })}>
                  <SelectTrigger data-testid="template-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="WHATSAPP">WhatsApp</SelectItem><SelectItem value="EMAIL">Email</SelectItem></SelectContent>
                </Select></div>
            </div>
            {f.channel === "EMAIL" && <div className="space-y-1.5"><Label>Subject</Label><Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="template-subject-input" /></div>}
            <div className="space-y-1.5"><Label>Message</Label><Textarea rows={7} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="template-body-input" /></div>
            <div className="flex flex-wrap gap-1.5">{data?.variables.map((v) => (
              <button key={v} type="button" onClick={() => insertVar(v)} className="font-mono text-[11px] rounded border px-1.5 py-0.5 hover:border-primary hover:text-primary" data-testid={`template-var-${v}`}>{`{{${v}}}`}</button>
            ))}</div>
            <Button onClick={save} data-testid="save-template-button">Save template</Button>
          </div>
        </Panel>
      )}
    </div>
  );
}
